import { MenuItem } from '@mui/material';
import Select, { SelectProps } from '@mui/material/Select';
import React from 'react';
import styled from 'styled-components';
import {
  darkTeal,
  lightTeal,
  materialUiDefaultBlack,
} from '../../theme/color-constants';
import { defaultTheme } from '../../theme/default-theme';

export const getSelectMenuItems = function <T>(
  items: Array<{
    label: string;
    value: T;
    key: string;
    disabled?: boolean;
    [key: string]: any;
  }>
) {
  return items.map(({ value, key, disabled, label, ...rest }) => (
    <MenuItem value={value as any} key={key} disabled={disabled} {...rest}>
      {label}
    </MenuItem>
  ));
};

export const DefaultSelect = styled(Select)<SelectProps>`
  min-width: 100%;
  max-width: 100%;
  border-radius: 5px;
  font-weight: 500;
  &:hover .MuiOutlinedInput-notchedOutline {
    border: 2px solid ${lightTeal};
  }

  &:hover {
    background-color: rgba(0, 165, 153, 0.04);
  }

  &:has(div[aria-expanded='true']) {
    border-radius: 5px 5px 0 0;
  }

  .MuiSelect-icon {
    font-size: 1.8rem;
    color: ${lightTeal};
    right: 9px;
  }

  fieldset,
  fieldset:hover {
    border: 2px solid ${lightTeal};
  }

  .MuiSelect-select {
    font-size: 14px;
    line-height: 16px;
    padding: 4px 10px;
  }
`;

DefaultSelect.defaultProps = {
  MenuProps: {
    PaperProps: {
      style: {
        maxHeight: '350px',
        overflow: 'auto',
        border: `2px solid ${lightTeal}`,
        borderTopWidth: '0px',
        borderRadius: '0px 0px 5px 5px',
      },
      sx: {
        '& .MuiMenuItem-root.Mui-selected': {
          backgroundColor: 'rgba(38, 166, 154, 0.5)',
        },
        '& .MuiMenuItem-root:hover': {
          backgroundColor: 'rgba(38, 166, 154, 0.2)',
        },
        '& .MuiMenuItem-root.Mui-selected:hover': {
          backgroundColor: lightTeal,
        },
        '.MuiList-root': {
          padding: 0,
        },
      },
    },
  },
};

export const InTableSelect = styled(Select)<SelectProps>`
  min-width: 100%;
  max-width: 100%;
  background-color: ${defaultTheme.color.clientPrimary};
  border-radius: 0;

  .MuiSelect-icon {
    font-size: 1.8rem;
    color: white;
  }

  .MuiSelect-select {
    color: white;
    padding-left: 15px;
  }

  fieldset {
    border: none;
  }
`;

InTableSelect.defaultProps = {
  MenuProps: {
    PaperProps: {
      style: {
        maxHeight: '350px',
        overflow: 'auto',
        boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
        borderTopWidth: '0px',
        borderRadius: '0px 0px 5px 5px',
        background: lightTeal,
        color: 'white',
      },
      sx: {
        '& .MuiMenuItem-root.Mui-selected': {
          // There is a weird bug where default select style has priority over this style - but only for default value and until something is clicked in dev tools
          // Therefore, I feel like !important which solves the issue is justified here.
          backgroundColor: `${defaultTheme.color.info} !important`,
        },
        '& .MuiMenuItem-root:hover': {
          backgroundColor: 'rgba(27, 113, 107, 0.6)',
        },
        '& .MuiMenuItem-root.Mui-selected:hover': {
          backgroundColor: `${darkTeal} !important`,
        },
        '.MuiList-root': {
          padding: 0,
        },
      },
    },
  },
};

export const InvisibleSelect = styled(Select)<SelectProps>`
  min-width: 100%;
  background-color: transparent;
  border-radius: 0;

  .MuiSelect-select {
    text-align: center;
    font-size: 14px;
    line-height: 20px;
  }

  .MuiSelect-icon {
    font-size: 1.7rem;
  }

  fieldset {
    border: none;
  }
`;

InvisibleSelect.defaultProps = {
  MenuProps: {
    PaperProps: {
      style: {
        maxHeight: '350px',
        overflow: 'auto',
        borderTopWidth: '0px',
        borderRadius: '0px 0px 5px 5px',
        background: 'white',
        boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
      },
      sx: {
        '& .MuiMenuItem-root.Mui-selected': {
          // There is a weird bug where default select style has priority over this style - but only for default value and until something is clicked in dev tools
          // Therefore, I feel like !important which solves the issue is justified here.
          backgroundColor: `${lightTeal} !important`,
        },
        '& .MuiMenuItem-root:hover': {
          backgroundColor: 'rgba(38, 166, 154, 0.5)',
        },
        '& .MuiMenuItem-root.Mui-selected:hover': {
          backgroundColor: 'rgba(38, 166, 154, 0.7) !important',
        },
        '.MuiList-root': {
          padding: 0,
        },
      },
    },
  },
};

export const SelectWithUnderline = styled(Select)<SelectProps>`
  min-width: 100%;
  background-color: transparent;
  border-radius: 0;
  border-bottom: 1px solid ${materialUiDefaultBlack};

  &:hover {
    border-bottom: 1px solid rgba(0, 0, 0, 0.5);
  }

  .MuiSelect-select {
    text-align: left;
    font-size: 14px;
    line-height: 20px;
    padding-left: 0;
  }

  .MuiSelect-icon {
    font-size: 1.7rem;
  }

  fieldset {
    border: none;
  }
`;

SelectWithUnderline.defaultProps = {
  MenuProps: {
    PaperProps: {
      style: {
        maxHeight: '350px',
        overflow: 'auto',
        borderTopWidth: '0px',
        borderRadius: '0px 0px 5px 5px',
        background: 'white',
        boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
      },
      sx: {
        '& .MuiMenuItem-root.Mui-selected': {
          // There is a weird bug where default select style has priority over this style - but only for default value and until something is clicked in dev tools
          // Therefore, I feel like !important which solves the issue is justified here.
          backgroundColor: `${lightTeal} !important`,
        },
        '& .MuiMenuItem-root:hover': {
          backgroundColor: 'rgba(38, 166, 154, 0.5)',
        },
        '& .MuiMenuItem-root.Mui-selected:hover': {
          backgroundColor: 'rgba(38, 166, 154, 0.7) !important',
        },
      },
    },
  },
};

export const SelectWithWhiteUnderline = styled(
  SelectWithUnderline
)<SelectProps>`
  border-bottom: 1px solid white;

  &:hover {
    border-bottom: 1px solid white;
  }

  .MuiSelect-select {
    text-align: left;
    font-size: 14px;
    line-height: 20px;
    padding-left: 0;
    color: white;
  }

  .MuiSelect-icon {
    font-size: 1.7rem;
    color: white;
  }
`;
